import { useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, FolderPlus } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { imageUrl } from '../../api/client';
import { setFavorite, setRating } from '../../api/images';
import { useAuth } from '../../hooks/useAuth';
import type { ImageSummary, UserName } from '../../api/types';

interface ImageCardProps {
  image: ImageSummary;
  shootId: string;
  index?: number;
  isFocused?: boolean;
  onMouseEnter?: () => void;
  // Anropas när användaren klickar på kortet (vänster-klick utan modifier).
  // Föräldern (Gallery) använder detta för att spara scroll-position innan
  // navigering. Om utelämnad faller vi tillbaka till plain navigate().
  onOpen?: (imageId: string) => void;
  // Anropas när användaren klickar "Add to set"-ikonen i top-right hover-overlay.
  // Föräldern (Gallery) öppnar AddToSetPopover med denna bild förvald.
  onAddToSet?: (imageId: string) => void;
  // Numerisk aspect ratio (width / height). Läses av MasonryGrid för
  // shortest-column-distribution. Om saknad → 0.66 (default porträtt).
  'data-aspect'?: number;
}

// En bild i masonry-griden. Behåller naturligt aspect ratio så att
// layout inte hoppar när bilden laddas. Bilden beskärs aldrig.
//
// Quick-actions: stjärnor + hjärta i hover-overlay (desktop) / alltid synlig
// på mobil. Klick på stjärnor/hjärta skickar mutation utan att navigera till
// detail view.
export function ImageCard({
  image,
  shootId,
  isFocused,
  onOpen,
  onAddToSet,
}: ImageCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userName = user?.name as UserName | undefined;

  const ratings = image.ratings ?? {};
  const favorites = image.favorites ?? {};

  // Optimistic local state för snabb feedback (hover-stjärnor)
  const myRating = userName ? ratings[userName] ?? 0 : 0;
  const myFavorite = userName ? favorites[userName] ?? false : false;
  const [hoverStars, setHoverStars] = useState<number | null>(null);

  const ratingMutation = useMutation({
    mutationFn: (stars: number) => setRating(image.id, stars),
    onMutate: async (stars) => {
      if (!userName) return;
      await queryClient.cancelQueries({ queryKey: ['shoot', shootId, 'images'] });
      const updates: { prev: unknown; key: readonly unknown[] }[] = [];
      const queries = queryClient.getQueriesData({ queryKey: ['shoot', shootId, 'images'] });
      for (const [key, prev] of queries) {
        updates.push({ prev, key });
        const list = prev as ImageSummary[] | undefined;
        if (!list) continue;
        queryClient.setQueryData(
          key,
          list.map((it) =>
            it.id === image.id
              ? { ...it, ratings: { ...(it.ratings ?? {}), [userName]: stars } }
              : it,
          ),
        );
      }
      return { updates };
    },
    onError: (_e, _v, ctx) => {
      ctx?.updates.forEach(({ key, prev }) => queryClient.setQueryData(key, prev));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shoot', shootId, 'images'] });
      queryClient.invalidateQueries({ queryKey: ['image', image.id] });
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: (favorite: boolean) => setFavorite(image.id, favorite),
    onMutate: async (favorite) => {
      if (!userName) return;
      await queryClient.cancelQueries({ queryKey: ['shoot', shootId, 'images'] });
      const queries = queryClient.getQueriesData({ queryKey: ['shoot', shootId, 'images'] });
      const updates: { prev: unknown; key: readonly unknown[] }[] = [];
      for (const [key, prev] of queries) {
        updates.push({ prev, key });
        const list = prev as ImageSummary[] | undefined;
        if (!list) continue;
        queryClient.setQueryData(
          key,
          list.map((it) =>
            it.id === image.id
              ? { ...it, favorites: { ...(it.favorites ?? {}), [userName]: favorite } }
              : it,
          ),
        );
      }
      return { updates };
    },
    onError: (_e, _v, ctx) => {
      ctx?.updates.forEach(({ key, prev }) => queryClient.setQueryData(key, prev));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shoot', shootId, 'images'] });
      queryClient.invalidateQueries({ queryKey: ['image', image.id] });
    },
  });

  function handleStarClick(e: MouseEvent, stars: number) {
    e.preventDefault();
    e.stopPropagation();
    // Klick på samma stjärna = toggle av (sätt 0)
    ratingMutation.mutate(myRating === stars ? 0 : stars);
  }

  function handleHeartClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    favoriteMutation.mutate(!myFavorite);
  }

  function handleCardClick(e: MouseEvent) {
    // Låt Cmd/Ctrl/Shift/middle-click använda <Link> i bakgrunden för
    // "öppna i ny flik" / "öppna i nytt fönster". Vi tar bara över vanliga
    // vänsterklick — då vill vi spara scroll och göra client-side nav.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (onOpen) onOpen(image.id);
    else navigate(`/shoots/${shootId}/images/${image.id}`);
  }

  const aspectRatio = image.width && image.height ? `${image.width} / ${image.height}` : '2 / 3';
  const displayStars = hoverStars ?? myRating;

  return (
    <div
      onClick={handleCardClick}
      className={[
        'relative block overflow-hidden rounded-md bg-loupe-bg-2 cursor-pointer aspect-stable',
        'transition-transform duration-200',
        'hover:-translate-y-0.5 group',
        isFocused ? 'ring-2 ring-henrik ring-offset-2 ring-offset-loupe-bg' : '',
      ].join(' ')}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Hidden link så Cmd-klick / höger-klick still works för "öppna i ny flik" */}
      <Link
        to={`/shoots/${shootId}/images/${image.id}`}
        className="absolute inset-0 z-0"
        tabIndex={-1}
        aria-label={image.filename}
      />

      <img
        src={imageUrl(image.id, 'preview')}
        alt={image.filename}
        loading="lazy"
        decoding="async"
        width={image.width}
        height={image.height}
        className="w-full h-auto block transition-[filter] duration-300 group-hover:brightness-105 relative pointer-events-none"
        style={{ aspectRatio }}
      />

      {/* Hover-overlay (gradient) — desktop only */}
      <div
        className={[
          'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200',
          'group-hover:opacity-100 hidden sm:block',
          'bg-gradient-to-b from-black/40 from-0% via-transparent via-25% to-black/85 to-100%',
        ].join(' ')}
      />

      {/* Top-overlays (filnamn + andras rating + add-to-set) — desktop hover */}
      <div className="hidden sm:flex absolute top-2 left-2 right-2 justify-between items-start opacity-0 group-hover:opacity-100 transition-opacity z-10 gap-2">
        {/* Vänster: filnamn + andras rating (stacked) */}
        <div className="flex flex-col gap-1 min-w-0 pointer-events-none">
          {/* Filnamn-pill */}
          <div className="inline-flex max-w-full items-center gap-1.5 bg-black/65 backdrop-blur px-2 py-0.5 rounded-full text-[10px] border border-white/10 font-mono tabular-nums">
            <span className="truncate text-white/90">{image.filename}</span>
          </div>
          {(['henrik', 'michelle'] as UserName[])
            .filter((u) => u !== userName && (ratings[u] ?? 0) > 0)
            .map((u) => (
              <RatingPill key={u} user={u} stars={ratings[u] ?? 0} />
            ))}
        </div>

        {/* Höger: add-to-set-knapp */}
        {onAddToSet ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToSet(image.id);
            }}
            aria-label="Lägg till i set"
            title="Lägg till i set"
            className="flex-shrink-0 bg-black/65 backdrop-blur-md rounded-full w-7 h-7 flex items-center justify-center border border-white/10 hover:scale-110 transition-transform text-white/80 hover:text-white"
          >
            <FolderPlus className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        ) : null}
      </div>

      {/* Quick rating + favorite overlay (din egen) — alltid synlig på mobil,
          fade in vid hover på desktop */}
      <div
        className={[
          'absolute bottom-0 left-0 right-0 z-20',
          'px-2 py-2 flex items-center justify-between gap-2',
          'sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200',
        ].join(' ')}
      >
        {/* Stjärnor */}
        <div
          className="flex items-center gap-0.5 bg-black/65 backdrop-blur-md rounded-full px-2 py-1 border border-white/10"
          onMouseLeave={() => setHoverStars(null)}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={(e) => handleStarClick(e, n)}
              onMouseEnter={() => setHoverStars(n)}
              aria-label={`Sätt ${n} stjärnor`}
              className="text-base leading-none px-0.5 transition-transform hover:scale-110"
            >
              <span
                className={
                  n <= displayStars
                    ? 'text-gold drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]'
                    : 'text-white/30 hover:text-white/60'
                }
              >
                ★
              </span>
            </button>
          ))}
        </div>

        {/* Favorit */}
        <button
          type="button"
          onClick={handleHeartClick}
          aria-label={myFavorite ? 'Ta bort favorit' : 'Markera som favorit'}
          className="bg-black/65 backdrop-blur-md rounded-full w-8 h-8 flex items-center justify-center border border-white/10 hover:scale-110 transition-transform"
        >
          <Heart
            className={`w-4 h-4 ${
              myFavorite ? 'fill-heart text-heart' : 'text-white/60'
            }`}
            strokeWidth={2}
          />
        </button>
      </div>

      {/* Andras favorit-prick (alltid synlig som indikator) */}
      <div className="absolute top-2 right-2 z-10 pointer-events-none flex flex-col gap-1">
        {(['henrik', 'michelle'] as UserName[])
          .filter((u) => u !== userName && favorites[u])
          .map((u) => (
            <span
              key={u}
              className={`w-2.5 h-2.5 rounded-full ring-2 ring-black/70 ${
                u === 'henrik' ? 'bg-henrik' : 'bg-michelle'
              }`}
              title={`${u === 'henrik' ? 'Henrik' : 'Michelle'} favorit`}
            />
          ))}
      </div>
    </div>
  );
}

function RatingPill({ user, stars }: { user: UserName; stars: number }) {
  return (
    <div className="flex items-center gap-1.5 bg-black/65 backdrop-blur px-2 py-0.5 rounded-full text-[10px] border border-white/10 tabular-nums">
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          user === 'henrik' ? 'bg-henrik' : 'bg-michelle'
        }`}
      />
      <span className="text-gold tracking-tighter">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className={i < stars ? '' : 'text-white/20'}>
            ★
          </span>
        ))}
      </span>
    </div>
  );
}
